import { NgFor, NgIf } from '@angular/common';
import { Component, DoCheck, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiService } from '../../core/api.service';
import { Cliente } from '../../core/models';

@Component({
  standalone: true,
  imports: [ReactiveFormsModule, NgFor, NgIf],
  template: `
    <header class="page-header">
      <div><h2>Clientes</h2><p>Cadastro dos clientes atendidos</p></div>
    </header>

    <nav class="tabs" aria-label="Abas de clientes">
      <button class="tab-button" type="button" [class.active]="abaAtiva === 'consulta'" (click)="selecionarAba('consulta')">Consulta</button>
      <button class="tab-button" type="button" [class.active]="abaAtiva === 'cadastro'" (click)="selecionarAba('cadastro')">{{ form.controls.id_cliente.value ? 'Editar cliente' : 'Cadastrar cliente' }}</button>
    </nav>

    <div class="toast-stack" *ngIf="erro || mensagem">
      <div class="toast" [class.toast-error]="erro" [class.toast-success]="!erro && mensagem" role="alert">
        <span>{{ erro || mensagem }}</span>
        <button class="toast-close" type="button" (click)="fecharAviso()" aria-label="Fechar mensagem">X</button>
      </div>
    </div>

    <section class="panel" *ngIf="abaAtiva === 'cadastro'">
      <form class="grid-form" [formGroup]="form" (ngSubmit)="salvar()" novalidate>
        <input type="hidden" formControlName="id_cliente">
        <label>Nome fantasia<input formControlName="nome"></label>
        <label>Razão social<input formControlName="razao_social"></label>
        <label>CNPJ<input formControlName="cnpj" inputmode="numeric"></label>
        <label>Inscrição estadual<input formControlName="inscricao_estadual"></label>
        <label>Contato<input formControlName="contato"></label>
        <label>Telefone<input formControlName="telefone" inputmode="tel"></label>
        <label>Email<input type="email" formControlName="email"></label>
        <label>CEP<input formControlName="cep" inputmode="numeric"></label>
        <label>Endereço<input formControlName="endereco"></label>
        <label>Número<input formControlName="numero"></label>
        <label>Complemento<input formControlName="complemento"></label>
        <label>Bairro<input formControlName="bairro"></label>
        <label>Cidade<input formControlName="cidade"></label>
        <label>UF<input formControlName="uf" maxlength="2"></label>
        <label class="full">Observação<textarea formControlName="observacao" rows="3"></textarea></label>
        <label class="check"><input type="checkbox" formControlName="ativo"> Ativo</label>
        <div class="form-actions full">
          <button type="submit" [disabled]="salvando">{{ salvando ? 'Salvando...' : 'Salvar cliente' }}</button>
          <button class="ghost" type="button" (click)="novoCliente()">Limpar</button>
        </div>
      </form>
    </section>

    <table class="data-table" *ngIf="abaAtiva === 'consulta'">
      <thead><tr><th>Cliente</th><th>CNPJ</th><th>Contato</th><th>Endereço</th><th>Status</th><th></th></tr></thead>
      <tbody>
        <tr *ngFor="let cliente of clientes">
          <td data-label="Cliente">
            <strong>{{ cliente.nome }}</strong>
            <span class="muted-line" *ngIf="cliente.razao_social">{{ cliente.razao_social }}</span>
            <span class="muted-line" *ngIf="cliente.email">{{ cliente.email }}</span>
          </td>
          <td data-label="CNPJ">{{ cliente.cnpj || cliente.documento }}</td>
          <td data-label="Contato">
            <span>{{ cliente.contato || '-' }}</span>
            <span class="muted-line" *ngIf="cliente.telefone">{{ cliente.telefone }}</span>
          </td>
          <td data-label="Endereço">
            <span>{{ enderecoResumo(cliente) || '-' }}</span>
            <span class="muted-line" *ngIf="cliente.cidade || cliente.uf">{{ cliente.cidade }}{{ cliente.uf ? '/' + cliente.uf : '' }}</span>
          </td>
          <td data-label="Status"><span class="status" [class.cancelada]="!cliente.ativo">{{ cliente.ativo ? 'Ativo' : 'Inativo' }}</span></td>
          <td class="actions" data-label="Ações">
            <button type="button" (click)="editar(cliente)">Editar</button>
            <button type="button" (click)="inativar(cliente)" [disabled]="!cliente.ativo">Inativar</button>
          </td>
        </tr>
      </tbody>
    </table>
  `
})
export class ClientesComponent implements OnInit, DoCheck, OnDestroy {
  private api = inject(ApiService);
  private fb = inject(FormBuilder);
  clientes: Cliente[] = [];
  erro = '';
  mensagem = '';
  salvando = false;
  abaAtiva: 'consulta' | 'cadastro' = 'consulta';
  private avisoAtual = '';
  private avisoTimer?: ReturnType<typeof setTimeout>;

  form = this.fb.nonNullable.group({
    id_cliente: [0],
    nome: ['', Validators.required],
    razao_social: [''],
    cnpj: [''],
    inscricao_estadual: [''],
    contato: [''],
    telefone: [''],
    email: ['', Validators.email],
    cep: [''],
    endereco: [''],
    numero: [''],
    complemento: [''],
    bairro: [''],
    cidade: [''],
    uf: [''],
    observacao: [''],
    ativo: [true]
  });

  ngOnInit() {
    this.carregar();
  }

  ngDoCheck() {
    const aviso = this.erro || this.mensagem;
    if (aviso === this.avisoAtual) return;

    this.avisoAtual = aviso;
    this.agendarFechamentoAviso(aviso);
  }

  ngOnDestroy() {
    if (this.avisoTimer) clearTimeout(this.avisoTimer);
  }

  carregar() {
    this.api.clientes().subscribe((clientes) => (this.clientes = clientes));
  }

  selecionarAba(aba: 'consulta' | 'cadastro') {
    this.abaAtiva = aba;
    this.erro = '';
    this.mensagem = '';
  }

  fecharAviso() {
    this.erro = '';
    this.mensagem = '';
    this.avisoAtual = '';
    if (this.avisoTimer) clearTimeout(this.avisoTimer);
  }

  salvar() {
    this.erro = '';
    this.mensagem = '';
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.erro = 'Revise os campos destacados antes de salvar';
      return;
    }

    this.salvando = true;
    const raw = this.form.getRawValue();
    const payload = {
      ...raw,
      id_cliente: raw.id_cliente || undefined,
      cnpj: this.onlyDigits(raw.cnpj),
      documento: this.onlyDigits(raw.cnpj),
      cep: this.onlyDigits(raw.cep),
      email: this.texto(raw.email).toLowerCase(),
      uf: this.texto(raw.uf).toUpperCase()
    };
    this.api.salvarCliente(payload).subscribe({
      next: (cliente) => {
        this.novoCliente();
        this.mensagem = `Cliente ${cliente.nome} salvo com sucesso`;
        this.abaAtiva = 'consulta';
        this.carregar();
      },
      error: (error) => {
        this.erro = error.error?.message || 'Erro ao salvar cliente';
        this.salvando = false;
      },
      complete: () => (this.salvando = false)
    });
  }

  editar(cliente: Cliente) {
    this.form.patchValue({
      id_cliente: cliente.id_cliente,
      nome: cliente.nome || '',
      razao_social: cliente.razao_social || '',
      cnpj: cliente.cnpj || cliente.documento || '',
      inscricao_estadual: cliente.inscricao_estadual || '',
      contato: cliente.contato || '',
      telefone: cliente.telefone || '',
      email: cliente.email || '',
      cep: cliente.cep || '',
      endereco: cliente.endereco || '',
      numero: cliente.numero || '',
      complemento: cliente.complemento || '',
      bairro: cliente.bairro || '',
      cidade: cliente.cidade || '',
      uf: cliente.uf || '',
      observacao: cliente.observacao || '',
      ativo: cliente.ativo !== false
    });
    this.abaAtiva = 'cadastro';
    this.erro = '';
    this.mensagem = '';
  }

  inativar(cliente: Cliente) {
    this.erro = '';
    this.mensagem = '';
    this.api.inativarCliente(cliente.id_cliente).subscribe({
      next: () => {
        this.mensagem = `Cliente ${cliente.nome} inativado com sucesso`;
        this.carregar();
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao inativar cliente')
    });
  }

  novoCliente() {
    this.erro = '';
    this.mensagem = '';
    this.form.reset({
      id_cliente: 0,
      nome: '',
      razao_social: '',
      cnpj: '',
      inscricao_estadual: '',
      contato: '',
      telefone: '',
      email: '',
      cep: '',
      endereco: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      uf: '',
      observacao: '',
      ativo: true
    });
  }

  enderecoResumo(cliente: Cliente) {
    return [cliente.endereco, cliente.numero, cliente.bairro].filter(Boolean).join(', ');
  }

  private onlyDigits(value: string) {
    return String(value || '').replace(/\D/g, '');
  }

  private texto(value: string | null | undefined) {
    return String(value || '').trim();
  }

  private agendarFechamentoAviso(aviso: string) {
    if (this.avisoTimer) clearTimeout(this.avisoTimer);
    if (!aviso) return;

    this.avisoTimer = setTimeout(() => this.fecharAviso(), 20000);
  }
}
